/**
 * ClassSubjectPicker — gives the teacher an explicit way to tell the
 * agent which class + subject the chat is about. Without this the
 * `default` agent has no domain context and free-form requests like
 * "generate a reading practice" pick an arbitrary subject (e.g. the
 * Chinese-school assumption produced a 语文 practice for an English
 * teacher's request).
 *
 * The pick is forwarded as runContext.school_class_id + .school_subject
 * on every chat message — JZ's agent prompts reference those fields to
 * scope outputs.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useClasses, type Subject } from "../../api/school";

interface Props {
  classId: string | null;
  onChange: (classId: string | null) => void;
}

export function ClassSubjectPicker({ classId, onChange }: Props): JSX.Element {
  const { t } = useTranslation();
  const classes = useClasses();

  const grouped = useMemo(() => {
    const bySubject: Record<Subject, { id: string; name: string }[]> = {
      english: [],
      math: [],
      chinese: [],
    };
    for (const c of classes.data ?? []) {
      bySubject[c.subject]?.push({ id: c.id, name: c.name });
    }
    return bySubject;
  }, [classes.data]);

  return (
    <label className="flex items-center gap-2 text-xs text-slate-600">
      <span>{t("chat.classContext.label")}</span>
      <select
        value={classId ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-800 focus:border-sky-300 focus:outline-none min-w-[180px]"
      >
        <option value="">{t("chat.classContext.none")}</option>
        {(Object.entries(grouped) as Array<[Subject, { id: string; name: string }[]]>)
          .filter(([, list]) => list.length > 0)
          .map(([subject, list]) => (
            <optgroup key={subject} label={t(`common.subjects.${subject}`)}>
              {list.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </optgroup>
          ))}
      </select>
    </label>
  );
}
